import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { Permission } from "../../../../interfaces/common/permissions";
import { Block, HowToReg } from "@mui/icons-material";

interface MatrixState {
  [model: string]: {
    create?: boolean;
    view?: boolean;
    update?: boolean;
    delete?: boolean;
  };
}

interface PermissionMatrixProps {
  permissions: Permission[]; // All available permissions
  selectedIds: number[]; // Which permission ids are selected
  onChange: (selectedPermissionIds: number[]) => void;
}

type ActionType = "create" | "view" | "update" | "delete"

const actions: ActionType[] = ["create", "view", "update", "delete"];

export const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  permissions,
  selectedIds,
  onChange,
}) => {


  const [selectedIdsInput, setSelectedIdsInput] = useState<number[]>(selectedIds)

  // Initialize matrix once from selectedIds
  const [matrix, setMatrix] = useState<MatrixState>(() => {
    const initial: MatrixState = {};
    permissions.forEach((p) => {
      if (actions.includes(p.action as ActionType)) {
        if (!initial[p.model]) initial[p.model] = {};
        initial[p.model][p.action as ActionType] = selectedIds.includes(p.id);
      }
    });
    return initial;
  });


  // Build a stable mapping of model -> {label, perms by action}
  const modelInfo = useMemo(() => {
    const info: Record<string, { label: string; perms: Record<ActionType, Permission | undefined> }> = {};
    permissions.forEach((p) => {
      if (!info[p.model]) {
        info[p.model] = {
          label: p.model_label,
          perms: { create: undefined, view: undefined, update: undefined, delete: undefined },
        };
      }
      if (actions.includes(p.action as ActionType)) {
        info[p.model].perms[p.action as ActionType] = p;
      }
    });
    return info;
  }, [permissions]);

  const models = Object.keys(modelInfo);

  useEffect(() => {
    const selected: number[] = [];
    models.forEach((model) => {
      actions.forEach((action) => {
        const perm = modelInfo[model]?.perms[action];
        if (perm && matrix[model]?.[action]) selected.push(perm.id);
      });
    });
    setSelectedIdsInput(selected);
  }, [matrix])

  useEffect(()=> onChange(selectedIdsInput), [JSON.stringify(selectedIdsInput)])

  // Toggle a single cell
  const toggleCell = useCallback((model: string, action: ActionType) => {
    setMatrix((prev) => ({
      ...prev,
      [model]: {
        ...prev[model],
        [action]: !prev[model]?.[action],
      },
    }));
  }, []);

  // Quick actions
  const setRow = (model: string, value: boolean) => {
    setMatrix((prev) => ({
      ...prev,
      [model]: { create: value, view: value, update: value, delete: value },
    }));
  };

  const setColumn = (action: ActionType, value: boolean) => {
    setMatrix((prev) => {
      const newState: MatrixState = {};
      models.forEach((model) => {
        newState[model] = { ...prev[model], [action]: value };
      });
      return { ...prev, ...newState };
    });
  };

  const setAll = (value: boolean) => {
    const newState: MatrixState = {};
    models.forEach((model) => {
      newState[model] = { create: value, view: value, update: value, delete: value };
    });
    setMatrix(newState);
  };

  return (
    <Box>
      <Box display="flex" gap={5} mb={1}>
        <Typography variant="h6">Resource Based</Typography>
        <Box display="flex" gap={2}>
          <Button startIcon={<HowToReg />} color="success" variant="outlined" size="small" onClick={() => setAll(true)}>
            Allow All
          </Button>
          <Button startIcon={<Block />} color="error" variant="outlined" size="small" onClick={() => setAll(false)}>
            Deny All
          </Button>
        </Box>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Model</TableCell>
            {actions.map((action) => (
              <TableCell key={action}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Typography sx={{ textTransform: "capitalize" }}>{action}</Typography>
                  <Box>
                    <IconButton size="small" onClick={() => setColumn(action, true)}>
                      <HowToReg color="success" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setColumn(action, false)}>
                      <Block color="error" />
                    </IconButton>
                  </Box>
                </Box>
              </TableCell>
            ))}
            <TableCell>Row</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {models.map((model) => (
            <TableRow key={model}>
              <TableCell>{modelInfo[model].label}</TableCell>
              {actions.map((action) => {
                const perm = modelInfo[model].perms[action];
                const checked = matrix[model]?.[action] || false;
                return (
                  <TableCell key={action} align="center">
                    {perm ? (
                      <Checkbox checked={checked} onChange={() => toggleCell(model, action)} />
                    ) : (
                      <span>-</span>
                    )}
                  </TableCell>
                );
              })}
              <TableCell>
                <Box display="flex" gap={1}>
                  <IconButton size="small" onClick={() => setRow(model, true)}>
                    <HowToReg color="success" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setRow(model, false)}>
                    <Block color="error" />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
